package org.example.project.ui.theme

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Typography
import androidx.compose.material3.lightColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

/**
 * Playful Studio Design System - Color Tokens
 * Derived from .ai/ui-design-brief.md and .ai/ui-design-direction.png
 */
object PlayfulColors {
    // Canvas & backgrounds
    val Background = Color(0xFFFFF9F2)
    val Surface = Color(0xFFFFFFFF)
    val SurfaceVariant = Color(0xFFF5EFE8)
    val SurfaceSubtle = Color(0xFFFAF5EE)

    // Text & Content
    val TextPrimary = Color(0xFF26344A)
    val TextSecondary = Color(0xFF5E6879)
    val TextMuted = Color(0xFF8A94A6)
    val TextDisabled = Color(0xFFB0B7C3)
    val TextOnPrimary = Color(0xFFFFFFFF)

    // Borders & Dividers
    val Border = Color(0xFFE4DCD4)
    val BorderSubtle = Color(0xFFF0EAE3)
    val BorderStrong = Color(0xFFCBD5E1)

    // Brand & Accents
    val Coral = Color(0xFFE96F55)
    val CoralDark = Color(0xFFD6583E)
    val CoralLight = Color(0xFFFDF0ED)
    val CoralBorder = Color(0xFFF9CEBA)

    val Teal = Color(0xFF3EA995)
    val TealDark = Color(0xFF2D8776)
    val TealLight = Color(0xFFEBF7F5)
    val TealBorder = Color(0xFFBCE6DE)

    val SunYellow = Color(0xFFF2C75C)
    val SunYellowDark = Color(0xFF8A6805)
    val SunYellowLight = Color(0xFFFEF9EB)
    val SunYellowBorder = Color(0xFFFDE6A5)

    val Lilac = Color(0xFF9381D0)
    val LilacDark = Color(0xFF5A499C)
    val LilacLight = Color(0xFFF4F1FA)
    val LilacBorder = Color(0xFFD6CFF0)

    // Semantic States
    val Success = Color(0xFF2E7D32)
    val SuccessContainer = Color(0xFFE8F5E9)
    val OnSuccessContainer = Color(0xFF1B5E20)
    val SuccessBorder = Color(0xFFA5D6A7)

    val Error = Color(0xFFD32F2F)
    val ErrorContainer = Color(0xFFFFEBEE)
    val OnErrorContainer = Color(0xFFB71C1C)
    val ErrorBorder = Color(0xFFFFCDD2)

    val Warning = Color(0xFFE65100)
    val WarningContainer = Color(0xFFFFF3E0)
    val OnWarningContainer = Color(0xFFBF360C)
    val WarningBorder = Color(0xFFFFE0B2)

    val Info = Color(0xFF0277BD)
    val InfoContainer = Color(0xFFE1F5FE)
    val OnInfoContainer = Color(0xFF01579B)
    val InfoBorder = Color(0xFFB3E5FC)
}

/**
 * Playful Studio Spacing Scale (multiples of 4dp / 8dp)
 */
@Immutable
data class PlayfulSpacingTokens(
    val xxs: Dp = 2.dp,
    val xs: Dp = 4.dp,
    val sm: Dp = 8.dp,
    val md: Dp = 12.dp,
    val lg: Dp = 16.dp,
    val xl: Dp = 20.dp,
    val xxl: Dp = 24.dp,
    val huge: Dp = 32.dp,
    val massive: Dp = 48.dp
)

/**
 * Playful Studio Shape Tokens
 */
@Immutable
data class PlayfulShapeTokens(
    val card: RoundedCornerShape = RoundedCornerShape(16.dp),
    val cardSmall: RoundedCornerShape = RoundedCornerShape(12.dp),
    val button: RoundedCornerShape = RoundedCornerShape(12.dp),
    val buttonSmall: RoundedCornerShape = RoundedCornerShape(8.dp),
    val input: RoundedCornerShape = RoundedCornerShape(12.dp),
    val pill: RoundedCornerShape = RoundedCornerShape(999.dp),
    val badge: RoundedCornerShape = RoundedCornerShape(8.dp),
    val dialog: RoundedCornerShape = RoundedCornerShape(20.dp)
)

val LocalPlayfulSpacing = staticCompositionLocalOf { PlayfulSpacingTokens() }
val LocalPlayfulShapes = staticCompositionLocalOf { PlayfulShapeTokens() }

private val PlayfulColorScheme = lightColorScheme(
    primary = PlayfulColors.Coral,
    onPrimary = PlayfulColors.TextOnPrimary,
    primaryContainer = PlayfulColors.CoralLight,
    onPrimaryContainer = PlayfulColors.CoralDark,
    inversePrimary = PlayfulColors.CoralLight,

    secondary = PlayfulColors.Teal,
    onSecondary = Color.White,
    secondaryContainer = PlayfulColors.TealLight,
    onSecondaryContainer = PlayfulColors.TealDark,

    tertiary = PlayfulColors.Lilac,
    onTertiary = Color.White,
    tertiaryContainer = PlayfulColors.LilacLight,
    onTertiaryContainer = PlayfulColors.LilacDark,

    background = PlayfulColors.Background,
    onBackground = PlayfulColors.TextPrimary,

    surface = PlayfulColors.Surface,
    onSurface = PlayfulColors.TextPrimary,
    surfaceVariant = PlayfulColors.SurfaceVariant,
    onSurfaceVariant = PlayfulColors.TextSecondary,
    surfaceTint = PlayfulColors.Coral,

    inverseSurface = PlayfulColors.TextPrimary,
    inverseOnSurface = PlayfulColors.Background,

    error = PlayfulColors.Error,
    onError = Color.White,
    errorContainer = PlayfulColors.ErrorContainer,
    onErrorContainer = PlayfulColors.OnErrorContainer,

    outline = PlayfulColors.Border,
    outlineVariant = PlayfulColors.BorderSubtle,
    scrim = Color(0x66000000)
)

private val PlayfulTypography = Typography(
    displayLarge = TextStyle(
        fontFamily = FontFamily.Default,
        fontWeight = FontWeight.Bold,
        fontSize = 32.sp,
        lineHeight = 40.sp,
        color = PlayfulColors.TextPrimary
    ),
    displayMedium = TextStyle(
        fontFamily = FontFamily.Default,
        fontWeight = FontWeight.Bold,
        fontSize = 28.sp,
        lineHeight = 36.sp,
        color = PlayfulColors.TextPrimary
    ),
    headlineLarge = TextStyle(
        fontFamily = FontFamily.Default,
        fontWeight = FontWeight.Bold,
        fontSize = 24.sp,
        lineHeight = 32.sp,
        color = PlayfulColors.TextPrimary
    ),
    headlineMedium = TextStyle(
        fontFamily = FontFamily.Default,
        fontWeight = FontWeight.SemiBold,
        fontSize = 20.sp,
        lineHeight = 28.sp,
        color = PlayfulColors.TextPrimary
    ),
    headlineSmall = TextStyle(
        fontFamily = FontFamily.Default,
        fontWeight = FontWeight.SemiBold,
        fontSize = 18.sp,
        lineHeight = 24.sp,
        color = PlayfulColors.TextPrimary
    ),
    titleLarge = TextStyle(
        fontFamily = FontFamily.Default,
        fontWeight = FontWeight.Bold,
        fontSize = 18.sp,
        lineHeight = 24.sp,
        color = PlayfulColors.TextPrimary
    ),
    titleMedium = TextStyle(
        fontFamily = FontFamily.Default,
        fontWeight = FontWeight.SemiBold,
        fontSize = 16.sp,
        lineHeight = 22.sp,
        color = PlayfulColors.TextPrimary
    ),
    titleSmall = TextStyle(
        fontFamily = FontFamily.Default,
        fontWeight = FontWeight.Medium,
        fontSize = 14.sp,
        lineHeight = 20.sp,
        color = PlayfulColors.TextPrimary
    ),
    bodyLarge = TextStyle(
        fontFamily = FontFamily.Default,
        fontWeight = FontWeight.Normal,
        fontSize = 15.sp,
        lineHeight = 22.sp,
        color = PlayfulColors.TextPrimary
    ),
    bodyMedium = TextStyle(
        fontFamily = FontFamily.Default,
        fontWeight = FontWeight.Normal,
        fontSize = 13.sp,
        lineHeight = 18.sp,
        color = PlayfulColors.TextSecondary
    ),
    bodySmall = TextStyle(
        fontFamily = FontFamily.Default,
        fontWeight = FontWeight.Normal,
        fontSize = 12.sp,
        lineHeight = 16.sp,
        color = PlayfulColors.TextSecondary
    ),
    labelLarge = TextStyle(
        fontFamily = FontFamily.Default,
        fontWeight = FontWeight.SemiBold,
        fontSize = 14.sp,
        lineHeight = 20.sp,
        color = PlayfulColors.TextPrimary
    ),
    labelMedium = TextStyle(
        fontFamily = FontFamily.Default,
        fontWeight = FontWeight.Medium,
        fontSize = 12.sp,
        lineHeight = 16.sp,
        color = PlayfulColors.TextSecondary
    ),
    labelSmall = TextStyle(
        fontFamily = FontFamily.Default,
        fontWeight = FontWeight.Medium,
        fontSize = 11.sp,
        lineHeight = 14.sp,
        color = PlayfulColors.TextMuted
    )
)

@Composable
fun PlayfulStudioTheme(
    content: @Composable () -> Unit
) {
    val spacing = PlayfulSpacingTokens()
    val shapes = PlayfulShapeTokens()

    CompositionLocalProvider(
        LocalPlayfulSpacing provides spacing,
        LocalPlayfulShapes provides shapes
    ) {
        MaterialTheme(
            colorScheme = PlayfulColorScheme,
            typography = PlayfulTypography,
            content = content
        )
    }
}
